/**
 * Created by Michał Stawski on 05.05.2020.
 */
import React from 'react'
import {
  QuestionInputText,
  QuestionMultipleValue,
  QuestionDate,
  QuestionDateRange,
  QuestionPointsGenerator,
  QuestionSelectOne,
  QuestionUploadFiles,
  returnTooltip,
  QuestionNumericSlider
} from './surveyQustions'
import { Trans, t } from '@lingui/macro'
import { ErrorMessage } from 'formik'
import { Card, Typography } from '@material-ui/core'
import {
  NumericFieldScoreConfig,
  SelectOptionScoreConfig,
  TextFieldScoreConfig
} from './ScoreCardCreator'

const fixedQuestionAnswers = {
  SELECT_YESNO: [t`Yes`, t`No`],
  FIVE_POINT_FREQUENCY: [
    t`Always`,
    t`Often`,
    t`Sometimes`,
    t`Seldom`,
    t`Never/Hardly Ever`
  ],
  FIVE_POINT_TIME: [
    t`All of the time`,
    t`A large part of the time`,
    t`Part of the time`,
    t`A small part of the time`,
    t`Not at all`
  ],
  FIVE_POINT_EXTENT: [
    t`To a Very Large Extent`,
    t`To a Large Extent`,
    t`Somewhat`,
    t`To a Small Extent`,
    t`To a Very Small Extent`
  ],
  FOUR_POINT_SATISFACTION: [
    t`Very Satisfied`,
    t`Satisfied`,
    t`Dissatisfied`,
    t`Very Dissatisfied`
  ]
}

// const surveyItemOptionsFields = [
//   'conditionalObject',
//   'conditionalObjectSubfield',
//   'conditionalObjectName',
//   'conditionalObjectField',
//   'conditionForObjectField',
//   'isLinked',
//   'linkedObject',
//   'linkedField',
//   'createCase',
//   'caseType',
//   'tooltipAtBottom',
//   'hasTooltip',
//   'nonApplicable',
//   'isRequired',
//   'skipInExport',
//   'skipInPrint',
//   'rows',
//   'min',
//   'max'
// ]

const questionTypes = {
  HEADER: {
    id: 'HEADER',
    text: <Trans>Header</Trans>,
    icon: 'title',
    class: () => ''
  },
  UPLOAD_FILES: {
    id: 'UPLOAD_FILES',
    text: <Trans>Question with file upload</Trans>,
    icon: 'file_upload',
    class: QuestionUploadFiles
  },
  SELECT_YESNO: {
    id: 'SELECT_YESNO',
    text: <Trans>Question with yes/no answer</Trans>,
    icon: 'thumbs_up_down',
    class: QuestionPointsGenerator(fixedQuestionAnswers.SELECT_YESNO),
    nonApplicableAsOption: true
  },
  SELECT_ONE: {
    id: 'SELECT_ONE',
    text: <Trans>Question with custom, single answer</Trans>,
    icon: 'more_vert',
    class: QuestionSelectOne,
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true
  },
  SELECT_ONE_WITH_OTHER: {
    id: 'SELECT_ONE_WITH_OTHER',
    text: <Trans>Question with custom, single answer</Trans>,
    icon: 'more_vert',
    class: QuestionSelectOne,
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true
  },
  SELECT_MULTIPLE: {
    id: 'SELECT_MULTIPLE',
    text: <Trans>Question with custom, multiple answers</Trans>,
    icon: 'more_vert',
    class: QuestionMultipleValue,
    scoreConfig: SelectOptionScoreConfig
  },
  FIVE_POINT_FREQUENCY: {
    id: 'FIVE_POINT_FREQUENCY',
    text: <Trans>Question measuring frequency</Trans>,
    icon: 'access_time',
    class: QuestionPointsGenerator(fixedQuestionAnswers.FIVE_POINT_FREQUENCY),
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true
  },
  FIVE_POINT_TIME: {
    id: 'FIVE_POINT_TIME',
    text: <Trans>Question measuring time commitment</Trans>,
    icon: 'access_time',
    class: QuestionPointsGenerator(fixedQuestionAnswers.FIVE_POINT_TIME),
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true
  },
  FIVE_POINT_EXTENT: {
    id: 'FIVE_POINT_EXTENT',
    text: <Trans>Question measuring extent</Trans>,
    icon: 'leaderboard',
    class: QuestionPointsGenerator(fixedQuestionAnswers.FIVE_POINT_EXTENT),
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true
  },
  FOUR_POINT_SATISFACTION: {
    id: 'FOUR_POINT_SATISFACTION',
    text: <Trans>Question measuring satisfaction</Trans>,
    icon: 'sentiment_very_satisfied',
    class: QuestionPointsGenerator(
      fixedQuestionAnswers.FOUR_POINT_SATISFACTION
    ),
    scoreConfig: SelectOptionScoreConfig,
    nonApplicableAsOption: true
  },
  INPUT_DATE: {
    id: 'INPUT_DATE',
    text: <Trans>Question with date answer</Trans>,
    icon: 'calendar_today',
    class: QuestionDate
  },
  INPUT_DATE_RANGE: {
    id: 'INPUT_DATE_RANGE',
    text: <Trans>Question with date range answer</Trans>,
    icon: 'date_range',
    class: QuestionDateRange
  },
  INPUT_TEXT: {
    id: 'INPUT_TEXT',
    text: <Trans>Question with short text answer</Trans>,
    icon: 'text_snippet',
    class: QuestionInputText,
    scoreConfig: TextFieldScoreConfig
  },
  INPUT_TEXTAREA: {
    id: 'INPUT_TEXTAREA',
    text: <Trans>Question with long text answer</Trans>,
    icon: 'text_snippet',
    class: QuestionInputText,
    scoreConfig: TextFieldScoreConfig
  },
  INPUT_NUMBER: {
    id: 'INPUT_NUMBER',
    text: <Trans>Question with numeric answer</Trans>,
    icon: 'looks_one',
    class: QuestionInputText,
    scoreConfig: NumericFieldScoreConfig
  },
  SLIDER: {
    id: 'SLIDER',
    text: <Trans>Question with numeric slider</Trans>,
    icon: 'linear_scale',
    class: QuestionNumericSlider,
    scoreConfig: NumericFieldScoreConfig
  }
}

const indexToFieldId = index => `field_${index}`
const indexToOption = index => `option_${index}`

const QuestionWidgetInner = props => {
  const {
    question,
    disabled,
    hideError,
    disableFields,
    index,
    errorIsArray,
    className,
    displayEN,
    questionValue,
    setFieldValue,
    surveyId,
    opportunity,
    entityFiles,
    network
  } = props

  const QuestionClass = questionTypes[question.type].class
  if (question.type === questionTypes.HEADER.id) {
    return null
  }
  const noNativeErrorClasses = [
    'SELECT_YESNO',
    'SELECT_ONE',
    'SELECT_MULTIPLE',
    'FIVE_POINT_FREQUENCY',
    'FIVE_POINT_TIME',
    'FIVE_POINT_EXTENT',
    'FOUR_POINT_SATISFACTION',
    // 'INPUT_DATE',
    // 'INPUT_DATE_RANGE',
    'SLIDER'
  ]
  const noError = disabled || hideError
  const noNativeError = Boolean(
    noNativeErrorClasses.includes(question.type) && !question.isGroup
  )
  const mainErrorForGroup = Boolean(question.isGroup && !errorIsArray)
  return (
    <Card className={className} elevation={6}>
      {QuestionClass && (
        <>
          <QuestionClass
            hideError={noError}
            surveyId={surveyId}
            network={network}
            entityFiles={entityFiles}
            opportunity={opportunity}
            disabled={disabled}
            nonApplicableDisabled={props.nonApplicableDisabled}
            nonApplicable={props.nonApplicable}
            completedSurveys={null} // disable completed surveys and with it prefil
            displayEN={displayEN}
            disableFields={disableFields}
            questionValue={questionValue}
            setFieldValue={setFieldValue}
            question={question}
            fieldId={index}
          />
          {Boolean(noNativeError || mainErrorForGroup) && !noError && (
            <ErrorMessage name={index}>
              {msg => (
                <p
                  style={{
                    color: '#f5543b',
                    fontSize: '0.75rem',
                    marginTop: 3,
                    marginLeft: 14,
                    textAlign: 'left'
                  }}
                >
                  {msg}
                </p>
              )}
            </ErrorMessage>
          )}
        </>
      )}
    </Card>
  )
}

const QuestionWidget = React.memo(QuestionWidgetInner)

export {
  questionTypes,
  QuestionWidget,
  indexToFieldId,
  indexToOption,
  fixedQuestionAnswers
}
